<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useAdminAccess } from '@/composables/useAdminAccess'
import { useAuthStore } from '@/stores/auth'
import { useOrgStore } from '@/stores/org'

const { t } = useI18n()
const router = useRouter()
const authStore = useAuthStore()
const orgStore = useOrgStore()
const { isSystemAdmin } = useAdminAccess()

const isMenuOpen = ref(false)
const isLoginMenuOpen = ref(false)
const isLoggingIn = ref(false)
const avatarError = ref(false)

// Reset avatar error when user changes
watch(
  () => authStore.user?.avatar,
  () => {
    avatarError.value = false
  }
)

// Show avatar only if URL exists and no loading error
const showAvatar = computed(() => authStore.user?.avatar && !avatarError.value)

function onAvatarError() {
  avatarError.value = true
}

// Доступные провайдеры авторизации
const authProviders = [
  {
    id: 'yandex',
    name: 'Яндекс',
    nameEn: 'Yandex',
    icon: 'mdi-alpha-y-circle',
    color: '#FC3F1D',
    enabled: true,
  },
  {
    id: 'keycloak',
    name: 'Keycloak',
    nameEn: 'Keycloak',
    icon: 'mdi-shield-account',
    color: '#4D9AE5',
    enabled: false, // TODO: включить после настройки
  },
  {
    id: 'google',
    name: 'Google',
    nameEn: 'Google',
    icon: 'mdi-google',
    color: '#4285F4',
    enabled: false,
  },
]

const enabledProviders = computed(() => authProviders.filter((p) => p.enabled))

const userInitials = computed(() => {
  if (!authStore.user?.displayName) return '?'
  const parts = authStore.user.displayName.split(' ')
  if (parts.length >= 2) {
    return parts[0][0] + parts[1][0]
  }
  return authStore.user.displayName.substring(0, 2).toUpperCase()
})

function handleLogin(providerId: string) {
  isLoggingIn.value = true
  isLoginMenuOpen.value = false

  try {
    switch (providerId) {
      case 'yandex':
        authStore.loginWithYandex(orgStore.currentOrg.slug)
        break
      case 'keycloak':
        // TODO: authStore.loginWithKeycloak(orgStore.currentOrg.slug)
        console.log('Keycloak login not implemented yet')
        isLoggingIn.value = false
        break
      default:
        console.warn('Unknown provider:', providerId)
        isLoggingIn.value = false
    }
  } catch (e) {
    console.error('Login error:', e)
    isLoggingIn.value = false
  }
}

function handleLogout() {
  isMenuOpen.value = false
  authStore.logout()
}

function goToAdmin() {
  isMenuOpen.value = false
  router.push('/admin')
}
</script>

<template>
  <div class="auth-button-wrapper">
    <!-- Authenticated: User menu -->
    <v-menu
      v-if="authStore.isAuthenticated"
      v-model="isMenuOpen"
      location="bottom end"
      :close-on-content-click="false"
      offset="8"
    >
      <template #activator="{ props }">
        <button
          v-bind="props"
          class="auth-avatar-btn"
          :class="{ 'is-open': isMenuOpen }"
        >
          <v-avatar v-if="showAvatar" size="36">
            <v-img
              :src="authStore.user?.avatar"
              :alt="authStore.user?.displayName"
              @error="onAvatarError"
            />
          </v-avatar>
          <v-avatar v-else color="primary" size="36">
            <span class="avatar-initials">{{ userInitials }}</span>
          </v-avatar>
        </button>
      </template>

      <div class="user-popup">
        <div class="user-popup-header">
          <v-avatar v-if="showAvatar" size="48">
            <v-img
              :src="authStore.user?.avatar"
              :alt="authStore.user?.displayName"
              @error="onAvatarError"
            />
          </v-avatar>
          <v-avatar v-else color="primary" size="48">
            <span class="avatar-initials-lg">{{ userInitials }}</span>
          </v-avatar>
          <div class="user-info">
            <h4>{{ authStore.user?.displayName }}</h4>
            <span class="user-email">{{ authStore.user?.email }}</span>
          </div>
        </div>

        <v-divider class="my-3" />

        <!-- Admin button - only for system admin -->
        <div v-if="isSystemAdmin" class="admin-action">
          <v-btn
            variant="tonal"
            color="primary"
            size="small"
            block
            @click="goToAdmin"
          >
            <v-icon start icon="mdi-shield-crown" />
            Администрирование
          </v-btn>
        </div>

        <v-divider v-if="isSystemAdmin" class="my-3" />

        <div class="user-actions">
          <v-btn
            variant="tonal"
            color="error"
            size="small"
            block
            @click="handleLogout"
          >
            <v-icon start icon="mdi-logout" />
            {{ t('auth.logout') }}
          </v-btn>
        </div>
      </div>
    </v-menu>

    <!-- Not authenticated: Login menu -->
    <v-menu
      v-else
      v-model="isLoginMenuOpen"
      location="bottom end"
      :close-on-content-click="false"
      offset="8"
    >
      <template #activator="{ props }">
        <button
          v-bind="props"
          class="auth-login-btn"
          :class="{ 'is-open': isLoginMenuOpen }"
          :disabled="isLoggingIn || authStore.loading"
        >
          <v-progress-circular
            v-if="isLoggingIn || authStore.loading"
            indeterminate
            size="16"
            width="2"
            class="mr-2"
          />
          <template v-else>
            <v-icon icon="mdi-login" size="18" />
            <span>{{ t('auth.login') }}</span>
            <v-icon icon="mdi-chevron-down" size="16" class="chevron" />
          </template>
        </button>
      </template>

      <div class="login-popup">
        <div class="login-popup-header">
          <span class="login-title">{{ t('auth.selectProvider') }}</span>
        </div>

        <div class="login-providers">
          <button
            v-for="provider in enabledProviders"
            :key="provider.id"
            class="provider-btn"
            @click="handleLogin(provider.id)"
          >
            <v-icon :icon="provider.icon" :color="provider.color" size="24" />
            <span>{{ provider.name }}</span>
          </button>
        </div>

        <template v-if="authProviders.some(p => !p.enabled)">
          <v-divider class="my-2" />
          <div class="login-providers-disabled">
            <button
              v-for="provider in authProviders.filter(p => !p.enabled)"
              :key="provider.id"
              class="provider-btn disabled"
              disabled
            >
              <v-icon :icon="provider.icon" size="24" class="disabled-icon" />
              <span>{{ provider.name }}</span>
              <span class="coming-soon">скоро</span>
            </button>
          </div>
        </template>
      </div>
    </v-menu>
  </div>
</template>

<style lang="scss" scoped>
.auth-button-wrapper {
  display: flex;
  align-items: center;
}

.auth-login-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;

  // Glassmorphism
  background: color-mix(in srgb, var(--tlh-primary) 15%, transparent);
  backdrop-filter: blur(10px);
  border: 1px solid color-mix(in srgb, var(--tlh-primary) 20%, transparent);
  color: rgb(var(--v-theme-on-surface));

  .v-theme--glassDark & {
    background: color-mix(in srgb, var(--tlh-primary) 20%, transparent);
    border-color: color-mix(in srgb, var(--tlh-primary) 30%, transparent);
  }

  &:hover:not(:disabled) {
    background: color-mix(in srgb, var(--tlh-primary) 25%, transparent);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px color-mix(in srgb, var(--tlh-primary) 20%, transparent);
  }

  &.is-open {
    background: color-mix(in srgb, var(--tlh-primary) 25%, transparent);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .chevron {
    opacity: 0.7;
    margin-left: -2px;
  }
}

.auth-avatar-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 2px;
  border-radius: 50%;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }

  &.is-open {
    .v-avatar {
      ring: 2px solid var(--tlh-primary);
    }
  }

  .v-avatar {
    border: 2px solid color-mix(in srgb, var(--tlh-primary) 40%, transparent);
  }
}

.avatar-initials {
  font-size: 0.8rem;
  font-weight: 600;
}

.avatar-initials-lg {
  font-size: 1rem;
  font-weight: 600;
}

.user-popup,
.login-popup {
  min-width: 240px;
  padding: 12px;
  border-radius: 12px;
  color: rgb(var(--v-theme-on-surface));

  // Light theme - explicit colors
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);

  // Dark theme
  .v-theme--glassDark & {
    background: rgba(30, 41, 59, 0.95);
    border-color: rgba(255, 255, 255, 0.1);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  }
}

.user-popup {
  padding: 16px;
}

.user-popup-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-info {
  overflow: hidden;

  h4 {
    font-size: 0.95rem;
    font-weight: 600;
    margin: 0;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.user-email {
  font-size: 0.75rem;
  opacity: 0.6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.user-actions {
  .v-btn {
    text-transform: none;
    font-weight: 500;
  }
}

.login-popup-header {
  padding: 4px 8px 8px;
}

.login-title {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.5;
}

.login-providers,
.login-providers-disabled {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.provider-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  text-align: left;
  transition: all 0.15s ease;
  color: rgb(var(--v-theme-on-surface));

  &:hover:not(:disabled) {
    background: rgba(var(--v-theme-on-surface), 0.06);
  }

  &:active:not(:disabled) {
    background: rgba(var(--v-theme-on-surface), 0.1);
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .disabled-icon {
    opacity: 0.4;
  }

  .coming-soon {
    margin-left: auto;
    font-size: 0.65rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    opacity: 0.5;
    padding: 2px 6px;
    border-radius: 4px;
    background: rgba(var(--v-theme-on-surface), 0.08);
  }
}
</style>
